import { useEffect, useId, useRef, useState } from "react"

// Every mounted disclosure's own close function. Opening one closes the rest.
//
// The tidiness argument for this is the weak one; the real one is CREDIBILITY
// (CP1 usability). All three case-file mounts pass the SAME sentence, so two
// notes open at once render an identical privacy assurance twice, ~200px apart
// — and a reassurance a reader sees duplicated verbatim reads as boilerplate,
// which is the one register this sentence cannot afford. Secondly, now that the
// note displaces rather than covers, opening a second one moves the FIRST one
// and its trigger, which is the genuinely harmful class of layout shift: the
// reader's finger ends up over different content than when they tapped.
//
// A module-scoped Set rather than a context: there is no provider to thread and
// no prop to drill. It is bundle-global, which is correct while these surfaces
// render one page at a time. Inertia SSR (act:7079ffd1) does not change that:
// the Node renderer's module state persists across requests, but the Set is
// only ever WRITTEN from pointer/focus handlers, which never run server-side —
// if that stops being true, or a share control is ever rendered inside a
// portal or a modal alongside another, revisit this.
const openNotes = new Set()

// The share control for the React public surfaces (act:46054438) — funnel
// landing, funnel confirmation, and every case-file mount. ONE implementation
// on purpose: the behavior contract below exists twice in this codebase (here
// and the hand-rolled nonce'd script in layouts/investigation.html.erb, which
// serves the server-rendered article — no React bundle there), and two copies
// is already the maximum the drift lock tolerates.
//
// BEHAVIOR-CONTRACT TWIN (act:46054438): layouts/investigation.html.erb.
// spec/frontend/share_contract_parity_spec.rb parses BOTH sources and fails on
// a divergence — change one side, change both. The contract:
//   feature-detect -> navigator.share -> swallow AbortError silently ->
//   clipboard fallback copying sentence AND link -> announced, timed,
//   same-node copied state -> manual third path when the clipboard itself
//   rejects (insecure context, denied permission, in-app WebViews — where
//   Meta ad traffic lands).
//
// navigator.share is called SYNCHRONOUSLY in the click stack — iOS consumes
// the transient activation across an await, so an async hop before share()
// throws NotAllowedError intermittently on exactly the manual-test device.
//
// Mantine's CopyButton is NOT available here: @mantine/core is deliberately
// not imported anywhere under pages/Funnel or pages/CaseFile (and
// ModalsProvider is admin-only), so this is hand-rolled.
//
// Accessibility contract (CP1): a real <button type="button"> (Space AND
// Enter; announces as a button, never a link); the role="status" region is
// ALWAYS rendered so it exists before any announcement (an injected live
// region is reliably ignored); both label states stay inside the SAME
// persistent button node, so focus survives the timed revert; the revert
// re-announces nothing; the manual third path announces AND receives focus.
//
// That clause used to end "over a reserved box, so … no layout shifts". The
// reservation was removed on operator instruction (act:1f67fb9c) and the
// sentence was left behind asserting a world that no longer exists. The copied
// state DOES shift the row now, ~19px for 4s, and that trade is stated in full
// at the constant below — see `.claude/rules/change-closure.md` for why a
// comment describing a retired invariant is worse than no comment.
//
// The share payload arrives server-resolved ({ url, sentence } — plain
// strings; the sentence is a FirmDetail setting). The tagged url reaches only
// navigator.share / the clipboard / selectable TEXT — never a DOM href
// (crawl hygiene: the funnel is crawlable even though noindexed).
//
// `reassurance` (act:671afd5c) turns the privacy line into a DISCLOSURE beside
// the trigger instead of standing body text — operator's decision, reaffirmed
// over the objection that a tooltip hides reassurance from the reader most
// likely to need it. That objection is answered rather than ignored: the
// sentence lives in an ALWAYS-RENDERED sr-only node that BOTH controls point at
// with aria-describedby, so a screen-reader user hears it on reaching the share
// button WITHOUT opening anything, while the visible note (aria-hidden, so it
// is never announced twice) is what sighted users toggle. Pass it only where a
// person actually has something to be reassured ABOUT — the case file and the
// confirmation page. On the anonymous funnel landing "nothing about you or your
// file" would INVENT the worry it exists to settle (CP1 anti-confirmation).
//
// STATE THE RESIDUAL HONESTLY, because the paragraph above used to read as
// though the objection had simply been answered (CP1 usability, act:1f67fb9c).
// It was answered for ONE population. A screen-reader user is told the sentence
// on arrival, unprompted. A SIGHTED reader gets a 16px grey circle and has to
// both guess it holds a privacy answer and be curious enough to open it — and
// the person hesitating over Share is precisely the one scanning for a reason
// to hesitate. So the reassurance reaches assistive tech unconditionally and
// sighted readers only if they probe. That is a known, accepted residual, not a
// solved problem; do not let the next session re-derive the objection from
// scratch because this comment sounded settled.
//
// WHERE IT MAY BE MOUNTED — a live constraint, not a coincidence. The note is
// in FLOW and opens beneath the trigger, so ShareAction must be the LAST inline
// content in its paragraph. All four mounts satisfy this today. Put prose after
// a ShareAction and that paragraph will split in half the moment a reader opens
// the note.
//
// Opens four ways because there is no hover on touch: click/tap PINS it, focus
// opens it, hover opens it, and Escape closes it and returns focus. The note
// lives inside the trigger's own wrapper, so travelling into it with the
// pointer never leaves the group — WCAG 1.4.13's hoverable/persistent/
// dismissible trio, all three. Mantine's Tooltip is NOT an option here for the
// same reason CopyButton isn't (see above).
//
// IT DISPLACES, IT DOES NOT COVER (act:1f67fb9c, cabinet-small-screen leading).
// This used to be a floating tooltip with a placement flip, a horizontal
// collision shift and a clamped tail. All of that is gone, because the goal it
// was chasing is unreachable by anything that floats: these triggers sit in a
// LIST of claim rows, so a bubble opening downward covers the next claim and one
// opening upward covers the claim the reader just read. There is no third
// direction. The note is now in FLOW — it pushes the rows below it down and
// covers nothing, at every width, with no breakpoint and no geometry.
//
// Do not "restore" the tooltip for desktop. It looked safe there only because
// the fixture's second row was short; a row whose status text runs as long as
// "Retainer signed 2026-07-09 Share ⓘ" sits squarely under the old bubble.
//
// The relationship to the trigger is carried by adjacency in TIME (it appears
// when you tap) and in SPACE (it opens immediately beneath), which is how every
// accordion on the web says the same thing — so it needs no tail, and the tail
// was what required the clamp math in the first place.
export default function ShareAction({
  share,
  label,
  ariaLabel,
  onShare,
  className = "",
  reassurance,
  // Compact mobile rendering (operator decision 2026-08-19, for the case-file
  // settled rows): pass `icon` (a glyph node, aria-hidden by the caller) and
  // the button renders the glyph below the sm: breakpoint and the word label
  // — underlined, as every text share trigger reads — at sm:+ where the row
  // has room to spare. The word is what keeps the control self-documenting
  // where space is free; the glyph is what lets a settled row hold ONE line
  // on a phone. Callers using `icon` should omit `underline` from className:
  // in this mode the underline belongs to the sm:+ word span, not the glyph.
  icon,
  // The third-path text is block-level by default, which is right everywhere it
  // has a paragraph to sit in. A mount inside a `flex items-center` row (the
  // funnel nav) needs to float it instead — hence the override, defaulted to
  // today's exact string so every existing mount stays byte-identical.
  manualClassName = "block mt-2 text-xs text-gray-600 select-all break-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded",
}) {
  const [state, setState] = useState("idle") // idle | copied | manual
  // Bumped on every clipboard rejection, and the focus effect keys on it — a
  // SECOND failed attempt leaves `state` already at "manual", so an effect
  // keyed on state alone would not re-fire and focus would not move.
  const [manualAttempt, setManualAttempt] = useState(0)
  const manualRef = useRef(null)
  const statusRef = useRef(null)
  const revertTimer = useRef(null)
  // The disclosure. `pinned` distinguishes a click/tap (which survives the
  // pointer leaving) from a hover (which does not) — without it, tapping on
  // touch opens it and the synthetic mouseleave immediately shuts it again.
  const [open, setOpen] = useState(false)
  const pinned = useRef(false)
  const noteRef = useRef(null) // the ⓘ trigger — Escape returns focus here
  const wrapRef = useRef(null) // trigger + bubble, the hover/outside-tap group
  const noteId = `${useId()}-share-note`
  // Hover INTENT, not raw hover. In flow the note changes the card's height, so
  // a pointer merely crossing the ⓘ on its way somewhere else would pump the
  // layout. A short dwell before opening and a short grace before closing fixes
  // that at the trigger rather than by weakening the treatment. The grace also
  // covers the 8px margin between the trigger's line box and the note: margins
  // generate no hit area, so travelling down into the note briefly leaves the
  // wrapper, and without the grace that reads as mouseleave and shuts it —
  // which would break WCAG 1.4.13's "hoverable". Click, tap and focus bypass
  // both timers and act instantly; only hover is deliberate about waiting.
  const hoverTimer = useRef(null)
  const panelRef = useRef(null)
  // Set by the DELIBERATE opens (click, tap, focus) and never by hover, so the
  // reveal below cannot be triggered by a pointer merely passing through.
  const revealOnOpen = useRef(false)

  useEffect(
    () => () => {
      clearTimeout(revertTimer.current)
      clearTimeout(hoverTimer.current)
    },
    [],
  )

  const cancelHoverIntent = () => clearTimeout(hoverTimer.current)
  const openAfterDwell = () => {
    cancelHoverIntent()
    hoverTimer.current = setTimeout(() => setOpen(true), 150)
  }
  const closeAfterGrace = () => {
    cancelHoverIntent()
    if (pinned.current) return
    // 250ms, not 120ms (CP1 usability). The grace covers the pointer's travel
    // from the trigger down into the note, and in flow that journey crosses the
    // row's own padding and the note's 8px top margin — neither of which
    // generates a hit area. A note that vanishes mid-travel reads as broken;
    // nobody perceives 250ms as sticky.
    hoverTimer.current = setTimeout(() => setOpen(false), 250)
  }

  // One open at a time — see `openNotes` at the top of the file for why.
  const closeSelf = useRef(null)
  if (!closeSelf.current) {
    closeSelf.current = () => {
      pinned.current = false
      setOpen(false)
    }
  }
  useEffect(() => {
    const me = closeSelf.current
    openNotes.add(me)
    return () => openNotes.delete(me)
  }, [])
  useEffect(() => {
    if (!open) return
    // On COMMIT of the open, not when hover-intent starts — otherwise a pointer
    // crossing the card closes the note the reader is currently reading.
    openNotes.forEach((close) => {
      if (close !== closeSelf.current) close()
    })
  }, [open])

  // Bring the note into view when the reader deliberately opened it.
  //
  // This is the replacement for the deleted upward flip, and it is strictly
  // better than what it replaces: the flip made room by painting over content
  // the reader had already read, whereas this moves the page to show them the
  // thing they just asked for — the behaviour every accordion has taught them.
  // It matters on exactly one mount and would be a regression without it: the
  // open-question card's share sits at the very BOTTOM of the card with nothing
  // beneath it, so its note opens below the fold.
  //
  // `block: "nearest"` does nothing when the note is already fully visible and
  // the minimum scroll when it is not. Deferred past the height transition
  // because scrolling to a zero-height box is a no-op.
  useEffect(() => {
    if (!open || !revealOnOpen.current) return undefined
    revealOnOpen.current = false
    const id = setTimeout(() => {
      if (!panelRef.current) return
      const still =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      panelRef.current.scrollIntoView({ block: "nearest", behavior: still ? "auto" : "smooth" })
    }, 220)
    return () => clearTimeout(id)
  }, [open])
  // biome-ignore lint/correctness/useExhaustiveDependencies: This hook specifies more dependencies than necessary: manualAttempt. these effects are ANALYTICS instruments on the funnel. Adding a dependency makes an effect RE-RUN, so on a trackEvent effect that is a duplicate conversion event — silent corruption of the numbers ad spend is set from, which nothing here would redden (the Ruby suite parses this source, it does not render it). Deliberate per-site, not deferred: see act:5be85a36 for the per-site plan and the browser check that can falsify it.
  useEffect(() => {
    if (state === "manual" && manualRef.current) manualRef.current.focus()
  }, [state, manualAttempt])
  // Escape closes from ANYWHERE — a keydown on the trigger alone would leave a
  // pinned bubble unclosable once focus moved on, and "dismissible without
  // moving the pointer" is the half of 1.4.13 that is easiest to ship broken.
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key !== "Escape") return
      // A hover-dwell timer already in flight would otherwise re-open the note
      // a moment after Escape dismissed it.
      clearTimeout(hoverTimer.current)
      pinned.current = false
      setOpen(false)
      if (noteRef.current) noteRef.current.focus()
    }
    // Tap-outside: touch has no mouseleave, so a pinned note would otherwise
    // stay open until the next tap ON it.
    const onDocPointer = (e) => {
      if (!pinned.current) return
      // biome-ignore lint/complexity/useOptionalChain: same cross-runtime contract as above — the clause spelling is probed in both runtimes and must match.
      if (wrapRef.current && wrapRef.current.contains(e.target)) return
      clearTimeout(hoverTimer.current)
      pinned.current = false
      setOpen(false)
    }
    document.addEventListener("keydown", onKey)
    document.addEventListener("click", onDocPointer)
    return () => {
      document.removeEventListener("keydown", onKey)
      document.removeEventListener("click", onDocPointer)
    }
  }, [open])

  if (!share) return null

  const announce = (message) => {
    if (statusRef.current) statusRef.current.textContent = message
  }

  const showCopied = () => {
    setState("copied")
    announce("Message and link copied.")
    clearTimeout(revertTimer.current)
    revertTimer.current = setTimeout(() => setState("idle"), 4000)
  }

  const showManual = () => {
    setState("manual")
    setManualAttempt((n) => n + 1)
    announce("Couldn't copy — the message and link are shown below.")
  }

  const copyFallback = (text, url) => {
    // biome-ignore lint/complexity/useOptionalChain: same cross-runtime contract as above — the clause spelling is probed in both runtimes and must match.
    if (!(navigator.clipboard && navigator.clipboard.writeText)) return showManual()
    // biome-ignore lint/style/useTemplate: half of a CROSS-RUNTIME CONTRACT: share_contract_parity_spec probes this source and its hand-written ERB twin for byte-identical clause spellings. Rewriting one runtime is the divergence that lock exists to catch.
    navigator.clipboard.writeText(text + " " + url).then(showCopied, showManual)
  }

  const activate = () => {
    const text = share.sentence
    const url = share.url
    // The note has done its job the moment they share — and on a phone the OS
    // sheet is about to cover the page anyway, so leaving it open just means
    // they come back to a card that is taller than the one they left.
    clearTimeout(hoverTimer.current)
    pinned.current = false
    setOpen(false)
    if (onShare) onShare()
    if (typeof navigator.share === "function") {
      // ONE member, deliberately — the sentence with the link folded into it.
      //
      // This used to be `{ text: text, url: url }`, which is the shape the Web
      // Share API is built for, and the sentence never arrived. Observed by the
      // operator 2026-08-12 sharing to Messages: the recipient got a bare link.
      // The OS share sheet takes the url member and discards the text, so the
      // firm's sentence — the entire point of this control, since a share with
      // no pre-filled text is a share we do not ship — was thrown away below
      // our code. copyFallback has always joined the two into one string, and
      // that asymmetry is the tell: the path we fall BACK to was already doing
      // what the primary path could not.
      //
      // WHAT THIS TRADES. The url member exists so a target can treat the link
      // AS a link, so AirDrop, Mail's subject/body split, and anything building
      // a rich attachment from url now see only text — AirDrop specifically is
      // reported to save a plain-text file rather than open the link, which is
      // the concrete cost of this trade. Messages linkifies the
      // URL in the body and the recipient still gets the preview card, which
      // the RECEIVING app fetches from the URL itself. Those other targets are
      // the residual risk and are exactly what the device check covers — the
      // OS-side behaviour cannot be verified from a headless session or from
      // the e2e suite (headless Chromium has no navigator.share at all).
      //
      // Two consequences before you edit this line. The browser no longer
      // validates the url — a url member that failed to parse used to reject
      // TypeError into the clipboard path, and now it just rides inside the
      // message — and WebIDL's absent-member forgiveness is gone: an undefined
      // member used to drop out of the payload and now concatenates as the
      // literal word "undefined". Neither is reachable today (every share
      // sentence refuses a blank write, and both presenters return payloads
      // that are nil-or-complete), which is what makes those upstream guards
      // load-bearing for the message's CONTENT now, not merely for whether
      // this control mounts.
      //
      // THE URL GOES LAST AND NOTHING FOLLOWS IT. Not style — it is what makes
      // the preview card appear. Messages renders a card only when the URL sits
      // at the very start or the very end of the message and is the only URL in
      // it; appending anything after it, even a full stop, suppresses the card.
      // So the sentence comes first, the link ends the string, and a future
      // edit that adds a sign-off after the link silently trades the card away.
      //
      // The separator is "\n\n" here and a single space in copyFallback. That
      // divergence is deliberate, not drift: this string is composed for a
      // message body, where the link wants its own line, while the clipboard's
      // is pasted into a field the sender is already typing in. The parity
      // spec pins each separately — do not "unify" them.
      // biome-ignore lint/style/useTemplate: half of a CROSS-RUNTIME CONTRACT: share_contract_parity_spec probes this source and its hand-written ERB twin for byte-identical clause spellings. Rewriting one runtime is the divergence that lock exists to catch.
      navigator.share({ text: text + "\n\n" + url }).catch((err) => {
        if (err && err.name === "AbortError") return // user cancelled — normal, never an error
        copyFallback(text, url)
      })
    } else {
      copyFallback(text, url)
    }
  }

  return (
    // whitespace-nowrap binds the share control to its ⓘ disclosure so a line
    // break can never fall BETWEEN them. Found the hard way (act:1f67fb9c):
    // narrowing the button's over-reserved width from 88px to 56px let it fit
    // up onto the preceding line beside the claim text, which orphaned the icon
    // alone on the next line — a worse separation than the 57px gap being
    // fixed. The pair is one affordance and must wrap as one.
    //
    // Safe for the in-sentence mounts (the landing and confirmation read
    // "Know someone this could help? <Share>"): this span contains only the
    // control group, so prose BEFORE it still wraps around it normally.
    //
    // It used to say prose "still wraps normally around it", full stop. That is
    // no longer true in both directions and it was measured false (CP1
    // anti-confirmation): the disclosure note is now a BLOCK box, so any text
    // placed AFTER a ShareAction is pushed onto its own line — open or closed.
    // All four mounts put the share last in its paragraph, so nothing renders
    // wrong today, and the component header states that as the live constraint
    // it now is. The sentence is corrected here rather than left standing
    // because this is the exact decision point where the next author adds a
    // word after a share control (`.claude/rules/change-closure.md`).
    // The manual third path inside is block-level and unaffected.
    <span className="inline whitespace-nowrap">
      <button
        type="button"
        onClick={activate}
        // A STATIC accessible name, always — defaulting to the idle label when
        // a caller passes none. Without it the copied state changes the
        // FOCUSED button's name, and screen readers announce a name change on
        // top of the polite live region (then again 4s later when it reverts).
        // The live region is the sole announcer; the visual swap is silent.
        aria-label={ariaLabel || label}
        // Points at the ALWAYS-rendered sr-only sentence below, so the
        // reassurance reaches assistive tech on arrival at the share control —
        // no interaction required. This is the whole answer to "a tooltip hides
        // it from the person who needs it most."
        aria-describedby={reassurance ? noteId : undefined}
        className={`relative whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 ${className}`}
      >
        {/* Both states live inside this one persistent node: the copied state
            overlays the idle label — no node swap, focus survives the timed
            revert. The check glyph is the non-colour cue.

            NO WIDTH RESERVATION — operator decision, 2026-08-11 (act:1f67fb9c):
            "there doesn't need to be room for copied. the icon should be
            closer." The button now sizes to its actual label.

            What that trades away, stated so nobody restores the reservation
            without knowing: this button used to carry min-w 5.5rem (88px) so
            the box was already wide enough for "✓ Copied" (51px measured, 12px
            Karla) and swapping states shifted nothing. Without it, a copy on a
            short label grows the button ~19px for the 4s the copied state
            lasts, nudging the ⓘ right and back. That was judged the better
            trade because the reservation's cost was CONSTANT and visible on
            every render — 56px of dead space wedged between the word "Share"
            and the disclosure meant to belong to it — while the shift is
            occasional, brief, and mostly desktop-only (on phones
            navigator.share opens the OS sheet and the copied state never
            fires at all). A permanent ugliness to avoid a transient one is a
            bad bargain. */}
        {icon ? (
          // ── Mobile-icon mode ──────────────────────────────────────────
          // The copied state REPLACES the showing label as plain content
          // instead of overlaying it: "✓ Copied" cannot fit inside an
          // 18px-wide box, and the brief width change is the same accepted
          // trade as the no-reservation decision above — occasional, 4s,
          // and mostly desktop-only (on phones navigator.share opens the
          // OS sheet and the copied state never fires). The button node
          // itself persists across the swap, so focus still survives the
          // timed revert, which is the half of the contract that matters.
          state === "copied" ? (
            <span aria-hidden="true">✓ Copied</span>
          ) : (
            <>
              <span className="sm:hidden" aria-hidden="true">
                {icon}
              </span>
              <span className="hidden sm:inline underline">{label}</span>
            </>
          )
        ) : (
          <>
            <span
              className={state === "copied" ? "invisible" : undefined}
              aria-hidden={state === "copied"}
            >
              {label}
            </span>
            {state === "copied" && (
              <span
                className="absolute inset-0 flex items-center justify-center"
                aria-hidden="true"
              >
                ✓ Copied
              </span>
            )}
          </>
        )}
      </button>
      {reassurance && (
        // `inline`, not `inline-flex`: an inline box still generates the hover
        // region and still `contains()` the note for the outside-tap test, but
        // it lets the note below resolve its containing block to the row's own
        // paragraph — which is what gives it the full row width instead of a
        // 240px slab floating over the next claim.
        // biome-ignore lint/a11y/noStaticElementInteractions: the <span> is deliberate: display:inline is what lets the note resolve its containing block to the row's own paragraph. The keyboard path is handled on the interactive child and was reviewed in act:1f67fb9c.
        <span
          ref={wrapRef}
          className="inline"
          onMouseEnter={openAfterDwell}
          onMouseLeave={closeAfterGrace}
          // FOCUS-OUT CLOSES IT, and this is not symmetry for its own sake
          // (CP1 anti-confirmation, act:1f67fb9c). There was an onFocus that
          // opened the note and nothing that ever closed it, so tabbing onto a
          // ⓘ and tabbing away left aria-expanded true and the note open for
          // the rest of the session. While the note FLOATED that was invisible
          // — an overlay nobody's layout felt. In flow it is permanent
          // displacement of everything below it, and on the case file it turns
          // a page that fits the viewport into a scrolling one, behind the
          // reader's back. The bug was pre-existing; the harm is created by
          // moving the note into flow, which is exactly the class of defect a
          // redesign ships and its own checks do not look for.
          //
          // Unconditional, deliberately — it does not exempt a pinned note. The
          // note holds nothing focusable (it is aria-hidden), so focus leaving
          // the wrapper always means the reader has left the control entirely.
          onBlur={(e) => {
            if (e.currentTarget.contains(e.relatedTarget)) return
            clearTimeout(hoverTimer.current)
            pinned.current = false
            setOpen(false)
          }}
        >
          <button
            type="button"
            ref={noteRef}
            aria-expanded={open}
            // A NAME, not a glyph: "ⓘ" alone announces as an unlabelled button
            // (or as its Unicode name, which is worse). The description above
            // carries the sentence itself.
            aria-label="What gets shared"
            aria-describedby={noteId}
            onClick={() => {
              cancelHoverIntent()
              pinned.current = !pinned.current
              revealOnOpen.current = pinned.current
              setOpen(pinned.current)
            }}
            onFocus={() => {
              cancelHoverIntent()
              revealOnOpen.current = true
              setOpen(true)
            }}
            // ml-1 (4px), not ml-px: with the button's width reservation gone
            // the icon would otherwise sit flush against the final letter and
            // read as part of the word. 4px is a deliberate hairline — close
            // enough to belong to "Share", far enough not to collide with it.
            //
            // THE HIT OVERLAY BLEEDS RIGHT AND UP ONLY — never left, and never
            // far down (act:1f67fb9c). It used to be a 40x40 box CENTRED on the
            // 16px glyph, which put 12px of it back over the word: measured on a
            // 390px render, "Share" spanned x288-320 and the overlay claimed
            // 312-352, so document.elementFromPoint reported the DISCLOSURE for
            // the last 8px of the word. Tapping the final letter of the primary
            // action opened a privacy note instead of sharing, and no check
            // could see it because both boxes were individually correct.
            // Still exactly 40x40 (16 + 24 right, 16 + 24 up), so the touch
            // floor is untouched; the bleed now lands in empty space beside and
            // above the icon instead of on its neighbour. Biased UP rather than
            // symmetrically because below the icon is where the note opens —
            // a downward bleed would overlap the note's own top edge and turn a
            // click there into a dismissal.
            className="relative ml-1 inline-flex h-4 w-4 items-center justify-center align-middle rounded-full
                       text-[13px] font-semibold leading-none text-gray-500 hover:text-action
                       after:absolute after:left-0 after:-right-6 after:-top-6 after:bottom-0
                       after:content-['']
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1"
          >
            <span aria-hidden="true">&#9432;</span>
          </button>
          {/* The VISIBLE note is decoration for AT — the sr-only node below is
              the one thing in the accessibility tree, so nothing is announced
              twice when it opens.

              WHY IT IS ALWAYS MOUNTED rather than `{open && …}`: a height that
              animates in BOTH directions needs a box to animate, and the
              grid-rows 0fr->1fr recipe is the one way to transition to and from
              `auto` height in CSS alone. Collapsed it contributes no height —
              verified on the live render, not assumed, because an inline
              wrapper inside a <p> is exactly where a stray zero-height box
              would perturb the row rhythm.

              whitespace-normal is LOAD-BEARING and is the root-cause fix for
              act:1f67fb9c. The wrapper span carries whitespace-nowrap to keep
              "Share" and its ⓘ on one line, and that inherits all the way down
              here. Until this class existed the sentence could not wrap at all:
              measured at 390px, its element box was a correct 240px while its
              GLYPHS ran to x=597 — 207px off the right edge of the screen. That
              single fact was BOTH of the defects handed to this lane. It was
              why the tooltip "did not fit" (on desktop its tail rendered as
              white text on the white card, i.e. invisible), and it was the
              entire "phantom horizontal overflow": documentElement.scrollWidth
              read 597, exactly the glyph box's right edge. An exhaustive
              element-box offender scan came back EMPTY because a text node has
              no element box, which is why it was mistaken for a pseudo-element
              or a transform. Remove this class and both return. */}
          <span
            aria-hidden="true"
            data-testid="share-note-visible"
            className={[
              "grid whitespace-normal",
              "transition-all duration-200 ease-out motion-reduce:transition-none",
              open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
            ].join(" ")}
          >
            <span className="min-h-0 overflow-hidden">
              {/* An ASIDE, not a tooltip. bg-ink + white + shadow-xl is the skin
                  of a box floating over unknown content; this one sits in its
                  own space on a calm page, and that skin made a privacy
                  reassurance the highest-contrast object on a legal case file —
                  it read as an alarm. A pale inset panel in the page's own
                  palette reads as what it is: an aside that answers a question.

                  13px, not 12px: this is the one line here that a worried person
                  is actually trying to read, so it steps UP from the 12px
                  metadata around it — while staying under the 14px claim text it
                  annotates, since a note louder than its subject reads as a
                  warning. font-normal/normal-case/text-left reset the row's own
                  semibold, the confirmation page's text-ink, and the
                  right-aligned interview card's text-right — three of the four
                  mounts inherit a different type context, so these are not
                  belt-and-braces.

                  The 2px rise is not decoration (CP1 usability): without motion
                  ON THE CONTENT, a note in this palette sits close enough to the
                  card's own footer text that the reader perceives the page
                  MOVING rather than the page ANSWERING. The ring is doing the
                  same work — it is what makes this an object rather than another
                  paragraph. Do not weaken either. */}
              <span
                ref={panelRef}
                className={[
                  // ASYMMETRIC by design: 4px above, 12px below. With no tail,
                  // proximity is the ONLY thing saying which row this note
                  // belongs to — and at an equal gap on both sides it read as a
                  // divider BETWEEN the two claims rather than as an answer
                  // attached to the one above. Gestalt proximity does the work
                  // the tail used to, and does it without any geometry.
                  // scroll-mb-6 keeps the deliberate-open reveal from parking
                  // the note flush against the bottom edge of the screen.
                  "mt-1 mb-3 scroll-mb-6 block max-w-[34rem] rounded-md bg-brand/5 px-3 py-2",
                  "text-left text-[13px] font-normal normal-case leading-relaxed",
                  "text-gray-700 ring-1 ring-inset ring-brand/10",
                  "transition-transform duration-200 ease-out motion-reduce:transition-none",
                  open ? "translate-y-0" : "-translate-y-0.5",
                ].join(" ")}
              >
                {reassurance}
              </span>
            </span>
          </span>
          <span id={noteId} className="sr-only">
            {reassurance}
          </span>
        </span>
      )}
      {/* Always-rendered live region — text is written, the node never moves. */}
      <span role="status" aria-live="polite" ref={statusRef} className="sr-only" />
      {state === "manual" && (
        <span
          ref={manualRef}
          tabIndex={-1}
          // Keeps a visible focus ring: this node receives focus
          // programmatically, so suppressing the indicator would strand a
          // sighted keyboard user with no idea where they are.
          className={manualClassName}
        >
          {share.sentence} {share.url}
        </span>
      )}
    </span>
  )
}
